"use client";

import { useWeb3 } from "logic/components";
import { useContractView, useContractViews } from "logic/hooks";
import { ReserveAccountAbi } from "logic/pages/LoansPage/contracts";
import { Loan } from "logic/pages/LoansPage/data/types";
import { APP_TOKENS, CHAINS_PEER } from "logic/utils";
import { useState } from "react";
import { Address, erc20Abi, zeroAddress } from "viem";

export type LoanDetailsState = ReturnType<typeof useLoanDetailsState>;

export function useLoanDetailsState(loan: Loan) {
  const { chainId } = loan;
  const { address: borrowerEoa = zeroAddress } = useWeb3();
  const reserveAccount = loan.borrower;

  const tokens = APP_TOKENS[chainId];
  const peerChainId = CHAINS_PEER[chainId];

  const { data: balances, isError: isBalancesError } = useContractViews({
    scopeKey: "loan-details-balances",
    allowFailure: false,
    contracts: [
      /* reserve account balances */
      balanceOf(tokens.usdai.main.address, reserveAccount, chainId),
      balanceOf(tokens.usdai.peer.address, reserveAccount, peerChainId),
      balanceOf(tokens.pyusd.main.address, reserveAccount, chainId),
      balanceOf(tokens.pyusd.peer.address, reserveAccount, peerChainId),
      /* eoa balances */
      balanceOf(tokens.usdai.main.address, borrowerEoa, chainId),
      balanceOf(tokens.usdai.peer.address, borrowerEoa, peerChainId),
      balanceOf(tokens.pyusd.main.address, borrowerEoa, chainId),
      balanceOf(tokens.pyusd.peer.address, borrowerEoa, peerChainId),
    ],
    watch: true,
    query: {
      select: (data) => ({
        reserve: {
          usdaiMain: data[0],
          usdaiPeer: data[1],
          pyusdMain: data[2],
          pyusdPeer: data[3],
        },
        eoa: {
          usdaiMain: data[4],
          usdaiPeer: data[5],
          pyusdMain: data[6],
          pyusdPeer: data[7],
        },
      }),
    },
  });

  // The RA holds `required` back for upcoming payments, so it is not spendable:
  // every balance the UI shows and every repayment plan works off
  // `usdaiMain - required`. Kept out of the multicall above so a legacy RA
  // without the getter degrades to 0 instead of failing all the balances.
  const { data: reserves, isError: isReservesError } = useContractView({
    address: reserveAccount,
    abi: ReserveAccountAbi,
    functionName: "reserves",
    args: [],
    chainId,
    watch: true,
    query: { enabled: reserveAccount !== zeroAddress },
  });
  const reservesRequired = isReservesError ? 0n : reserves?.[0];

  const [isWithdrawModalOpen, setIsWithdrawModalOpen] = useState(false);
  const [isBridgeModalOpen, setIsBridgeModalOpen] = useState(false);

  return {
    loan,
    chainId,
    peerChainId,
    reserveAccount,
    tokens,
    balances: !isBalancesError ? balances : null,
    reservesRequired,
    isWithdrawModalOpen,
    setIsWithdrawModalOpen,
    isBridgeModalOpen,
    setIsBridgeModalOpen,
  };
}

function balanceOf(token: Address, account: Address, chainId: number) {
  return {
    address: token,
    abi: erc20Abi,
    functionName: "balanceOf",
    args: [account],
    chainId,
  } as const;
}
